import { isNotDefined, type DashboardDataWithEditable, type DashboardId, type UserId } from '@terros/common'
import { getHustleScopeRank, sortDashboardsByOrder } from '@front/model'

export type DashboardGroup = 'normal' | 'hustle'
export type DashboardGroups = Record<DashboardGroup, DashboardDataWithEditable[]>

export function getDashboardGroup(dashboard: DashboardDataWithEditable): DashboardGroup {
  return dashboard.home === true ? 'hustle' : 'normal'
}

export function getOrderedDashboardGroups(
  dashboards: DashboardDataWithEditable[],
  pendingOrders: Map<DashboardId, number>,
  viewerUserId?: UserId
): DashboardGroups {
  const ordered = dashboards
    .map((dashboard) => {
      const pendingOrder = pendingOrders.get(dashboard.dashboardId)
      if (isNotDefined(pendingOrder)) return dashboard
      return { ...dashboard, order: pendingOrder }
    })
    .sort(sortDashboardsByOrder)

  const hustle = ordered.filter((dashboard) => getDashboardGroup(dashboard) === 'hustle')
  hustle.sort(
    (left, right) => getHustleScopeRank(left.scopeId, viewerUserId) - getHustleScopeRank(right.scopeId, viewerUserId)
  )

  return {
    normal: ordered.filter((dashboard) => getDashboardGroup(dashboard) === 'normal'),
    hustle,
  }
}

export function getDisplayedDashboardGroups(
  groups: DashboardGroups,
  draggingDashboard: DashboardDataWithEditable | undefined,
  dragToIndex: number | undefined
): DashboardGroups {
  const displayGroup = (group: DashboardGroup): DashboardDataWithEditable[] => {
    const dashboards = groups[group]
    if (isNotDefined(draggingDashboard) || isNotDefined(dragToIndex)) return dashboards
    if (getDashboardGroup(draggingDashboard) !== group) return dashboards

    const reordered = dashboards.filter((dashboard) => dashboard.dashboardId !== draggingDashboard.dashboardId)
    reordered.splice(dragToIndex, 0, draggingDashboard)
    return reordered
  }

  return {
    normal: displayGroup('normal'),
    hustle: displayGroup('hustle'),
  }
}

export function flattenDashboardGroups(groups: DashboardGroups): DashboardDataWithEditable[] {
  return [...groups.hustle, ...groups.normal]
}
